// Everything the driver record needs: the loads, the derived profile, and the four
// writes (decide a document, act on standing, correct fields, upload on their behalf).
//
// Extracted from this folder's index.js so the merged user page can host the same
// record inside its Driver tab without a second copy. Two copies of document-decision
// and standing logic would drift, and this is the half where drifting is expensive.
//
// Deliberately NOT a layout: the hook owns state and writes, the caller owns chrome.
// That is what lets the standalone page render its own header while the user page
// renders tabs above the very same record.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import api from '../../lib/api';
import useQuery from '../../hooks/useQuery';
import useToast from '../../hooks/useToast';
import { patchSearch } from '../../helpers/utils';
import { adminApplicationStatusFor, buildProfile, eventLog, stateLabel } from './model';
import { pairFields, entryFor, DOCUMENT, DRIVER, VEHICLE } from './fields';

/** The vehicle a requirement hangs off, or null for a driver-level one. */
const vehicleOf = (profile, requirement) =>
  requirement?.driverVehicleId
    ? profile.vehicles.find((v) => v.driverVehicleId === requirement.driverVehicleId) || null
    : null;

/**
 * Every line AddressUpdateInput takes, and nothing else — `__typename` off the
 * GraphQL read included, which the input would reject.
 */
const ADDRESS_LINES = ['state', 'city', 'line1', 'line2', 'postal_code'];

/**
 * The address to send: what is stored, with the corrected lines over the top.
 *
 * WHOLE-OBJECT, because the mutation replaces the sub-object rather than patching it.
 * Sending only the corrected line would blank the rest, and every line but `line2` is
 * required, so an absent one is a GraphQL error rather than a silent gap.
 *
 * Nulls become ''. A driver with no line 1 on file still has to send one to correct
 * their ZIP, and '' is the only way to say "nothing here" that a required String
 * accepts. It is also how a reviewer CLEARS a line, since an emptied field arrives
 * here as null — so the two cases want the same value anyway.
 */
export const mergeAddress = (stored, edits) => {
  const merged = { ...(stored || {}), ...edits };
  return Object.fromEntries(ADDRESS_LINES.map((key) => [key, merged[key] == null ? '' : merged[key]]));
};

/**
 * A vehicle is added once the POST returns. Standing is a follow-up read: if that
 * read is temporarily unavailable, the dialog must still close rather than invite
 * a second POST for the vehicle that was already created.
 */
export const addVehicleAndRefreshStanding = async ({
  addVehicle,
  getStanding,
  setRecord,
  setStanding,
}) => {
  const nextRecord = await addVehicle();
  setRecord(nextRecord);
  try {
    setStanding(await getStanding());
  } catch (e) {
    // The record returned by the mutation is authoritative for the add. A later
    // page reload refreshes standing; failing this secondary read is not a failed add.
  }
  return nextRecord;
};

/**
 * What to tell the reviewer after a re-evaluation.
 *
 * Re-evaluation asks a question rather than asserting an answer, so the outcome is the
 * only thing worth reporting — including "unchanged", which is what a suspended driver
 * and a still-valid one both produce, for different reasons.
 */
export const reevaluateStandingOutcome = (before, after) => {
  if (!after) return 'Standing re-evaluated';
  const label = stateLabel({ hasBeenApproved: true, value: after }).toLowerCase();
  return before === after ? `Re-evaluated — still ${label}` : `Standing re-evaluated — now ${label}`;
};

/** Whether one of these requirement lists holds the open requirement. */
const contains = (list, id) => list.some((r) => r.id === id);

/**
 * Resolve whichever Mongo id the caller arrived with into the `driver_id` the
 * document and standing endpoints address drivers by.
 *
 * One user document carries two ObjectIds — its own `_id` (the Mongo user id)
 * and a separate `driver_id` field — and which one a link carries depends on
 * where it came from. The approvals grid has `driver_id`; the document review
 * queue returns `driverMongoId`, which is the Postgres mirror of `_id`. Without
 * this, half the entry points 404.
 *
 * `user(id)` matches on `_id`, so a hit means the route param was a user id and
 * `driver_id` is what we actually want. A miss means it already was a
 * `driver_id`, so it passes through untouched. Failures pass through too: the
 * query throws on a non-ObjectId, and the endpoint's own 404 is a better error
 * than one from a lookup the caller never asked for.
 */
export const resolveDriverId = async (routeId) => {
  try {
    const user = await api.users.getUser({ id: routeId });
    return { driverId: user?.driver_id || routeId, user: user || null };
  } catch (e) {
    return { driverId: routeId, user: null };
  }
};

/**
 * @param routeId       whichever id the caller arrived with, resolved internally.
 * @param knownDriverId the already-resolved `driver_id`, when the caller has it. The
 *                      user page does — it has the whole user document — so passing it
 *                      skips a round-trip that would otherwise repeat a lookup the page
 *                      has already done.
 */
const useDriverRecord = ({ routeId, knownDriverId = null } = {}) => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();

  // The resolved `driver_id`. Every write goes through this, never routeId.
  const [driverId, setDriverId] = useState(knownDriverId);
  // The Mongo user `_id`, which is what /users/:id is keyed on — a different id
  // again from the driver_id the document endpoints use.
  const [userId, setUserId] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [driver, setDriver] = useState(null);
  const [record, setRecord] = useState(null);
  const [standing, setStanding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [screening, setScreening] = useState(null);
  const [screeningError, setScreeningError] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const fileInput = useRef(null);

  // Which requirement is open, which version is on screen, and which list mode —
  // all in the URL rather than component state, so a reviewer can send someone
  // the exact document they are looking at instead of describing how to get
  // there. `item` also survives a reload, which matters when a decision fails and
  // the page is refreshed.
  const { item: selectedId = null, v: viewingId = null, mode = 'all' } = useQuery();

  const setSearch = (patch, { replace = false } = {}) => {
    const search = patchSearch(location.search, patch);
    // Opening a requirement is navigation — back should return to the record, which
    // is what both the inspector's chevron and the browser's own do. Closing one is
    // the pop of that push, not a push of its own; see closeItem. Changing the
    // version or the list mode is a view preference, and pushing those would bury
    // the record several entries deep in history.
    (replace ? history.replace : history.push)({ search });
  };

  // Selecting a different requirement resets the version, which belongs to the
  // one being left.
  const select = (id) => setSearch({ item: id, v: null });

  /**
   * Close the inspector by POPPING the entry that opened it, not by pushing a
   * clean one.
   *
   * Opening pushes `?item=A`. Pushing the item-less URL to close it left three
   * entries — record, item, record — so the browser's own back button went
   * *forward* into the document that had just been closed. The in-app chevron and
   * the browser chevron disagreed, which is worse than either being wrong.
   *
   * `location.key` is undefined for the first entry in a session, so on a pasted or
   * bookmarked link to a specific document there is nothing to pop; replace instead,
   * which leaves the record as the only entry rather than exiting the app.
   */
  const closeItem = () =>
    location.key ? history.goBack() : setSearch({ item: null, v: null }, { replace: true });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Sequential, because everything below is keyed on the resolved id. The
      // user lookup also carries the avatar, which the drivers query does not
      // return at all. Skipped entirely when the caller already knows the id.
      let resolved = knownDriverId;
      if (!resolved) {
        const lookup = await resolveDriverId(routeId);
        resolved = lookup.driverId;
        setUserId(lookup.user?.id || null);
        setPhotoUrl(lookup.user?.imageUrl || null);
      }
      setDriverId(resolved);

      // All three are needed for a coherent page, so a failure in any of them is
      // an error rather than a half-loaded profile.
      const [driverResult, recordResult, standingResult] = await Promise.all([
        api.drivers.getDriver(resolved),
        api.driverProfile.getRecord(resolved),
        api.driverProfile.getStanding(resolved),
      ]);
      setDriver(driverResult);
      setRecord(recordResult);
      setStanding(standingResult);

      // Fetched after, and its failure is confined to its own card. The history is
      // reference material — losing it should not cost a reviewer the document work
      // the rest of this page is for.
      api.driverProfile
        .getScreeningHistory(resolved)
        .then(setScreening)
        .catch((e) =>
          setScreeningError(e?.response?.data?.message || 'Could not load the screening history'),
        );
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Could not load this driver');
    } finally {
      setLoading(false);
    }
  }, [routeId, knownDriverId]);

  useEffect(() => {
    load();
  }, [load]);

  // buildProfile handles null record and standing, so this is safe while still loading.
  const profile = buildProfile({ record, standing, driver });
  const selected = profile.requirements.find((r) => r.id === selectedId) || null;

  // The extraction is fetched per open document rather than with the record, which
  // would need one query per row to carry it. Keyed by document id so a stale
  // response from a document you have already left cannot land on this one.
  const openDocumentId = selected?.current?.id ?? null;
  const refreshExtraction = useCallback(async () => {
    if (!driverId || !openDocumentId) {
      setExtraction(null);
      return;
    }
    setExtractionLoading(true);
    setExtractionError(null);
    try {
      const result = await api.driverProfile.getExtraction(driverId, openDocumentId);
      setExtraction({ documentId: openDocumentId, ...result });
    } catch (e) {
      setExtractionError(e?.response?.data?.message || 'Could not load what was extracted');
    } finally {
      setExtractionLoading(false);
    }
  }, [driverId, openDocumentId]);

  useEffect(() => {
    refreshExtraction();
  }, [refreshExtraction]);

  // `standing`, not `profile`: the standing trail arrives on the breakdown response
  // and buildProfile keeps only what the record cards render. Passed raw so a
  // standing action's own response — which returns the refreshed history — puts the
  // change in the log without a reload.
  const events = eventLog({ driver, profile, standing });

  // Stepping walks the actionable items — the point is to clear a queue, and
  // stepping through valid ones would be stepping through nothing to do. But a
  // valid requirement opened deliberately is not in that list, and stepping from
  // it would jump to an unrelated item and read "0 of 3"; browsing steps through
  // everything instead.
  //
  // In review mode it walks only our own queue, matching the list that was open —
  // stepping out of the filter into a document waiting on the driver is exactly the
  // conflation the filter exists to undo. Each list is used only if it actually holds
  // the selection, or the counter reads "0 of 3" and stepping jumps somewhere else.
  const steppable =
    mode === 'review' && contains(profile.reviewQueue, selectedId)
      ? profile.reviewQueue
      : contains(profile.actions, selectedId)
      ? profile.actions
      : profile.requirements;
  const stepIndex = selected ? steppable.findIndex((r) => r.id === selected.id) : -1;

  // Ignore a response belonging to a document that is no longer open.
  const currentExtraction = extraction?.documentId === openDocumentId ? extraction : null;
  const fieldRows = selected
    ? pairFields({
        documentType: selected.documentType,
        // Insurance covers several vehicles, so the entry is matched on the VIN of the
        // vehicle this document hangs off — see entryFor, which applies the same rule
        // to each model's own entries so the per-field candidates compare like with
        // like rather than one model's answer for a different car.
        entry: entryFor({
          documentType: selected.documentType,
          entries: currentExtraction?.latest?.entries,
          vin: vehicleOf(profile, selected)?.vin,
        }),
        models: currentExtraction?.latest?.models,
        vin: vehicleOf(profile, selected)?.vin,
        driver,
        vehicle: vehicleOf(profile, selected),
        documentRow: selected.current,
      })
    : [];

  const step = (delta) => {
    const next = steppable[stepIndex + delta];
    // Replace, not push: stepping through a queue of six documents should leave
    // one history entry to go back from, not six.
    if (next) setSearch({ item: next.id, v: null }, { replace: true });
  };

  const decide = async (body) => {
    setBusy(true);
    try {
      // The response IS the refreshed record — readiness and roll-ups are derived
      // server-side, so replace rather than patching the row in place.
      setRecord(await api.driverProfile.decideDocument(driverId, selected.current.id, body));
      // Standing follows the decision server-side; re-read so the pill and the
      // blocking count reflect it rather than lagging a click behind.
      setStanding(await api.driverProfile.getStanding(driverId));
      toast.success(`${selected.label} set to ${body.status.toLowerCase().replace(/_/g, ' ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not save that decision');
    } finally {
      setBusy(false);
    }
  };

  const actOnStanding = async (state, reason) => {
    setBusy(true);
    try {
      setStanding(await api.driverProfile.actOnStanding(driverId, state.value, reason));
      // A standing action can change what documents are required of them, so the
      // record is re-read too rather than assumed unchanged.
      setRecord(await api.driverProfile.getRecord(driverId));
      toast.success(`Standing set to ${state.label.toLowerCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not change standing');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Re-ask the gate.
   *
   * Not a decision, so there is no confirmation step and no reason field: it stores what
   * the driver's current documents and screening add up to. It can still move standing —
   * a cleared driver whose licence expired lands on invalid information — which is why
   * the toast reports where they ended up.
   *
   * No `getRecord` re-read, unlike `actOnStanding`. A standing action can change which
   * documents are required of a driver; this one only reads them.
   */
  const reevaluateStanding = async () => {
    setBusy(true);
    const before = standing?.currentStanding || null;
    try {
      const next = await api.driverProfile.reevaluateStanding(driverId);
      setStanding(next);
      toast.success(reevaluateStandingOutcome(before, next?.currentStanding || null));
    } catch (e) {
      // Also the backstop if the menu's copy of the server's rules ever drifts from the
      // rules themselves — the server's own message is what a reviewer sees.
      toast.error(e?.response?.data?.message || e?.message || 'Could not re-evaluate standing');
    } finally {
      setBusy(false);
    }
  };

  const applyStatusOverride = async (state, label) => {
    setBusy(true);
    try {
      const driverApplicationStatus = adminApplicationStatusFor(state.value);
      if (!driverApplicationStatus) throw new Error(`Unsupported ${label.toLowerCase()}`);
      setDriver(await api.drivers.updateDriver(driverId, { driverApplicationStatus }));
      try {
        setStanding(await api.driverProfile.getStanding(driverId));
      } catch (e) {
        toast.info(`${label} changed. Reload to refresh standing.`);
        return;
      }
      toast.success(`${label} set to ${state.label.toLowerCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || `Could not change ${label.toLowerCase()}`);
    } finally {
      setBusy(false);
    }
  };

  const actOnApplication = (state) => applyStatusOverride(state, 'Application status');

  /**
   * Apply field corrections, then re-read.
   *
   * Grouped by where each value lives, because they go to different services:
   * vehicle facts to the vehicle PATCH which dual-writes into Mongo, profile facts
   * to updateDriver. One save, two write paths, because that is where the data is.
   *
   * Returns whether it succeeded. FieldsPanel clears its open drafts on a success and
   * must NOT clear them on a failure — those drafts are the reviewer's unsaved typing,
   * so dropping them would make a transient API error cost the work.
   */
  const saveFields = async (edits) => {
    if (!edits.length) return false;
    setBusy(true);
    try {
      const vehicleFields = {};
      const documentFields = {};
      // Nested so `address.line1` lands as address: { line1 }, and only the keys
      // being corrected are sent — updateDriver replaces what it is given.
      const driverFields = {};
      edits.forEach(({ source, path, value }) => {
        const trimmed = value.trim() === '' ? null : value.trim();
        if (source === VEHICLE) {
          vehicleFields[path] = trimmed;
          return;
        }
        if (source === DOCUMENT) {
          documentFields[path] = trimmed;
          return;
        }
        if (source !== DRIVER) return;
        const [head, tail] = path.split('.');
        if (tail) {
          driverFields[head] = { ...(driverFields[head] || {}), [tail]: trimmed };
        } else {
          driverFields[head] = trimmed;
        }
      });

      if (Object.keys(documentFields).length && selected.current) {
        // No status: this corrects the date, it does not decide the document. An
        // EXPIRED row is expired because of the date, and EXPIRED cannot be sent
        // as a status — sending a decidable one would silently un-approve it.
        setRecord(
          await api.driverProfile.decideDocument(driverId, selected.current.id, {
            expirationDate: documentFields.expirationDate ?? null,
          }),
        );
      }
      if (Object.keys(vehicleFields).length && selected.driverVehicleId) {
        setRecord(
          await api.driverProfile.updateVehicle(driverId, selected.driverVehicleId, vehicleFields),
        );
      }
      if (Object.keys(driverFields).length) {
        // The address sub-object is replaced wholesale by this mutation, so the
        // untouched lines are carried over rather than dropped.
        //
        if (driverFields.address) {
          driverFields.address = mergeAddress(driver?.address, driverFields.address);
        }
        setDriver(await api.drivers.updateDriver(driverId, driverFields));
      }
      await refreshExtraction();
      setStanding(await api.driverProfile.getStanding(driverId));
      toast.success('Saved');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not save those fields');
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Delete a vehicle. The caller confirms; this does the write.
   *
   * Closes the inspector when the open requirement hung off this vehicle — its
   * document is gone, and leaving the panel on it would show a row that no longer
   * exists with decision buttons that would 404.
   */
  const removeVehicle = async (driverVehicleId) => {
    setBusy(true);
    try {
      setRecord(await api.driverProfile.deleteVehicle(driverId, driverVehicleId));
      if (selected?.driverVehicleId === driverVehicleId) {
        setSearch({ item: null, v: null }, { replace: true });
      }
      setStanding(await api.driverProfile.getStanding(driverId));
      toast.success('Vehicle deleted');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not delete that vehicle');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Grant or revoke this driver's exception to a retired vehicle model.
   *
   * Re-reads standing afterwards like every other write here, and for one more
   * reason: the exception changes whether the driver has a ready vehicle, so the
   * server re-derives standing and reconciles `driver_vehicle.status` on the way
   * through. Without the re-read the card would keep showing the record status the
   * exception just made stale.
   */
  const setVehicleGrandfathered = async (driverVehicleId, isGrandfathered) => {
    setBusy(true);
    try {
      setRecord(await api.driverProfile.updateVehicle(driverId, driverVehicleId, { isGrandfathered }));
      setStanding(await api.driverProfile.getStanding(driverId));
      toast.success(isGrandfathered ? 'Driver grandfathered' : 'Exception revoked');
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not change that exception');
    } finally {
      setBusy(false);
    }
  };

  /** Add a catalog vehicle and replace the record with the server's refreshed view. */
  const addVehicle = async (input) => {
    setBusy(true);
    try {
      await addVehicleAndRefreshStanding({
        addVehicle: () => api.driverProfile.addVehicle(driverId, input),
        getStanding: () => api.driverProfile.getStanding(driverId),
        setRecord,
        setStanding,
      });
      toast.success('Vehicle added');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not add that vehicle');
      return false;
    } finally {
      setBusy(false);
    }
  };

  /**
   * Re-run the pipeline over the open document.
   *
   * Asynchronous on the far side, so there is nothing to replace the record with —
   * this re-reads instead, once, after a beat. The beat is a guess and deliberately
   * a short one: the extraction is three model calls and will not be done, so the
   * re-read is there to pick up a fast decision replay and to prove the row moved,
   * not to poll the pipeline to completion. If it is still unchanged the reviewer
   * reloads, which is honest about what we know.
   */
  const replay = async (stage) => {
    if (!selected?.current) return;
    setBusy(true);
    try {
      await api.driverProfile.replayDocument(driverId, selected.current.id, stage);
      toast.success(
        stage === 'extraction'
          ? 'Queued for re-extraction — the models take a few seconds'
          : 'Queued for a re-decision from the stored extraction',
      );
      // ponytail: one delayed re-read, not a poll. A subscription or a status
      // endpoint is the real fix if reviewers start watching for the outcome.
      await new Promise((resolve) => setTimeout(resolve, 2500));
      setRecord(await api.driverProfile.getRecord(driverId));
      setStanding(await api.driverProfile.getStanding(driverId));
      await refreshExtraction();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Could not queue that re-run');
    } finally {
      setBusy(false);
    }
  };

  const pickFile = () => fileInput.current?.click();

  const upload = async (file) => {
    if (!file || !selected) return;
    setBusy(true);
    try {
      // Presign, PUT the bytes, then record the row. The last step is the one the
      // old admin path was missing — without it the file existed in the bucket and
      // in no document hub.
      const { url, fileName, fileUrl } = await api.driverProfile.getUploadPolicy(driverId);
      const response = await fetch(url, {
        method: 'PUT',
        body: new File([file], fileName, { type: 'image/jpeg' }),
        // Hardcoded, not file.type: the presigned PUT is signed over
        // ContentType: image/jpeg, so any other value fails the signature with a
        // 403. The picker is restricted to JPEG to match — widening it needs the
        // presign to take a content type, which is a backend change.
        headers: { 'Content-Type': 'image/jpeg' },
      });
      // fetch only rejects on network failure, so a 403 from an expired policy
      // would otherwise sail through and record a row pointing at no object.
      if (!response.ok) {
        throw new Error(`Upload to storage failed (${response.status})`);
      }
      setRecord(
        await api.driverProfile.addDocument(driverId, {
          documentType: selected.documentType,
          // `fileUrl` is the canonical object URL the server minted alongside the
          // policy — not the presigned one, whose query string is a credential.
          imageUrl: fileUrl,
          ...(selected.level === 'vehicle' ? { driverVehicleId: selected.driverVehicleId } : {}),
        }),
      );
      setStanding(await api.driverProfile.getStanding(driverId));
      toast.success('Uploaded — it will be verified like a driver upload');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Could not upload that file');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return {
    driverId,
    userId,
    photoUrl,
    driver,
    record,
    standing,
    profile,
    events,
    loading,
    error,
    busy,
    reload: load,
    screening,
    screeningError,
    selected,
    viewingId,
    mode,
    setSearch,
    select,
    closeItem,
    step,
    stepIndex,
    stepCount: steppable.length,
    extraction: currentExtraction,
    extractionLoading,
    extractionError,
    fieldRows,
    decide,
    replay,
    actOnStanding,
    reevaluateStanding,
    actOnApplication,
    saveFields,
    addVehicle,
    removeVehicle,
    setVehicleGrandfathered,
    pickFile,
    fileInput,
    upload,
  };
};

export default useDriverRecord;
